import type { ElementType} from 'react';
import React, { Fragment, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { ListItemTextProps} from '@mui/material';
import { Box, Divider, ListItemText, useMediaQuery, useTheme } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import { Link } from 'react-router-dom';
import { useRouteMatch } from 'react-router';

import * as Icons from '@mui/icons-material';

import {
  MenuToggleIconButton,
  StyledDrawer,
  StyledList,
  StyledListItem,
  StyledListItemIcon
} from './styles.ts';

import * as GuiStateSelectors from 'app/selectors/guiStateSelectors.ts';
import * as ScenarioSelectors from 'app/selectors/scenarioSelectors.ts';
import * as UserSelectors from 'app/selectors/userSelectors.ts';
import ScenarioSection from './ScenarioSection/scenarioSection.tsx';

import * as ScenarioActions from '../../actions/scenarios/scenarioAction.ts';
import AppRoutes from '../../utils/routes.ts';

import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/i18n/types/i18nNamespace.ts';
import type { I18nCommonNs } from '@/i18n/dictionaries/interfaces.ts';
import { isUserAnyAdminSelector, isUserOwnerSelector, isUserSuperAdminSelector } from 'app/selectors/userSelectors.ts';
import { GuiStateActions } from 'app/actions/guiState/guiStateAction.ts';
import greyLogo from '/icons/logo.svg?url';
import greyLogoMinimal from '/icons/logo-minimal.svg?url';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useLogout } from 'app/hooks/useLogout.ts';
import StatisticsSection from './StatisticsSection/StatisticsSection.tsx';


export default function NavigationDrawer(): JSX.Element {
  const [translate] = useTranslation(I18nNamespace.Common);
  const theme = useTheme();
  const logout = useLogout();
  const dispatch = useDispatch();
  const sideBarOpenState = useSelector(GuiStateSelectors.sideBarStateSelector);
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const isUserSuperAdmin = useSelector(isUserSuperAdminSelector);
  const isUserOwner = useSelector(isUserOwnerSelector);
  const isUserAnyAdmin = useSelector(isUserAnyAdminSelector);



  const currentUser = useSelector(UserSelectors.CurrentUserSelector);
  const hasSkllsAccess = !!(currentUser && currentUser.hasSklls === true);
  const hasInterpreterAccess = !!(currentUser && currentUser.hasInterpreter === true);
  const hasExercisesAccess = !!(currentUser && currentUser.hasExercises === true);
  const scenarios = useSelector(ScenarioSelectors.scenariosSelector)
  const getScenarios = useCallback(() => dispatch(ScenarioActions.getScenarios()), [dispatch]);


  useEffect(() => {
      if (currentUser && scenarios.length === 0) {
        getScenarios();
      }
    }, [getScenarios, currentUser, scenarios]);
  

  const toggleDrawer = () => {
    dispatch(GuiStateActions.sideMenuOpen(!sideBarOpenState));
  };


  const usersRouteMatch = useRouteMatch({
    path: AppRoutes.UserManagement,
    strict: false
  });

  const groupManagementRouteMatch = useRouteMatch({
    path: AppRoutes.GroupManagement,
    strict: false
  });

  const introductionRouteMatch = useRouteMatch({
    path: AppRoutes.Introduction,
    strict: false
  });

  const dashboardRouteMatch = useRouteMatch({
    path: AppRoutes.Dashboard,
    strict: false
  });

  const conversationsRouteMatch = useRouteMatch({
    path: AppRoutes.Conversations,
    strict: false
  });

  const courseManagementRouteMatch = useRouteMatch({
    path: AppRoutes.CourseManagement,
    strict: false
  });

  const myCoursesRouteMatch = useRouteMatch({
    path: AppRoutes.MyCourses,
    strict: false
  });

  const globalAssetsLibraryRouteMatch = useRouteMatch({
    path: AppRoutes.GlobalAssetsLibrary,
    strict: false
  });

  const callsRouteMatch = useRouteMatch({
    path: AppRoutes.CallManagement,
    strict: false
  });

  const userGuidesRouteMatch = useRouteMatch({
    path: AppRoutes.UserGuides,
    strict: false
  });

  const translatorRouteMatch = useRouteMatch({
    path: AppRoutes.Translator,
    strict: false
  });

  const exercisesRouteMatch = useRouteMatch({
    path: AppRoutes.ExercisesManager,
    strict: false
  });

  const failedJobsRouteMatch = useRouteMatch({
    path: AppRoutes.RecentJobs,
        strict: false
  });
  
  const companyManagementRouteMatch = useRouteMatch({
    path: AppRoutes.CompaniesManagement,
    strict: false
  });

  const scenariosRouteMatch = useRouteMatch({
    path: AppRoutes.Scenarios,
    strict: false
  });

  const evaluationProtocolsRouteMatch = useRouteMatch({
    path: AppRoutes.EvaluationProtocolManager,
    strict: false
  });

  const personasRouteMatch = useRouteMatch({
    path: AppRoutes.PersonasManager,
    strict: false
  });

  const DrawerListItemText = (props: ListItemTextProps) => {
    if (!sideBarOpenState) {
      return null;
    }
    return <ListItemText {...props} />;
  }

  return (
    <Fragment>
      <StyledDrawer
        variant="permanent"
        anchor="left"
        id="sideBarState"
        open={sideBarOpenState}
        data-cy={sideBarOpenState}
      >

        <Box sx={{ position: 'relative' }}>
          <MenuToggleIconButton
            onClick={toggleDrawer}
            data-cy="sidebarWrapper"
            size="small"
            sx={{ right: sideBarOpenState ? -20 : -40, }}
            aria-label={sideBarOpenState ? translate(nameof.full<I18nCommonNs>((l) => l.navigation.close)) : translate(nameof.full<I18nCommonNs>((l) => l.navigation.open))}
          >
            {!sideBarOpenState ? <ChevronRight /> : <ChevronLeft />}
          </MenuToggleIconButton>
        </Box>

        <Box
          component={Link}
          to={AppRoutes.Dashboard}
          sx={{ margin: isSmallScreen ? '19px 40px 0 72px' : '24px 40px 0 36px' }}>
          <img src={(sideBarOpenState || isSmallScreen) ? greyLogo : greyLogoMinimal} height={34} />
        </Box>


        <Box sx={{ overflow: 'auto', flexGrow: 1 }}>
          <StyledList subheader={<li />} sx={{
            alignItems: sideBarOpenState ? 'normal' : 'flex-start',
            paddingRight: sideBarOpenState ? theme.spacing(2) : 0,
          }}>
            {hasSkllsAccess && (
              <StyledListItem
                selected={!!dashboardRouteMatch}
                data-cy="dashboard"
                component={Link as ElementType}
                to={AppRoutes.Dashboard}
              >
                <StyledListItemIcon>
                  <Icons.Dashboard />
                </StyledListItemIcon>
                <DrawerListItemText primary={translate(nameof.full<I18nCommonNs>((l) => l.navigation.dashboard))} />
              </StyledListItem>
            )}


            {hasSkllsAccess && (isUserAnyAdmin ? (
              <ScenarioSection />
            ) : (
              <StyledListItem
                selected={!!scenariosRouteMatch}
                data-cy="scenarios"
                component={Link as ElementType}
                to={AppRoutes.Scenarios}
              >
                <StyledListItemIcon>
                  <Icons.ViewList />
                </StyledListItemIcon>
                <DrawerListItemText primary={translate(nameof.full<I18nCommonNs>((l) => l.navigation.scenarios))} />
              </StyledListItem>
            ))}

            {hasSkllsAccess && (
              <StyledListItem
                selected={!!conversationsRouteMatch}
                data-cy="conversations"
                component={Link as ElementType}
                to={AppRoutes.Conversations}
              >
                <StyledListItemIcon>
                  <Icons.History />
                </StyledListItemIcon>
                <DrawerListItemText primary={translate(nameof.full<I18nCommonNs>((l) => l.navigation.conversations))} />
              </StyledListItem>
            )}
            
            {isUserAnyAdmin && hasSkllsAccess && (
              <StyledListItem
                selected={!!usersRouteMatch}
                component={Link as ElementType}
                to={AppRoutes.UserManagement}
              >
                <StyledListItemIcon>
                  <Icons.SupervisorAccount />
                </StyledListItemIcon>
                <DrawerListItemText primary={translate(nameof.full<I18nCommonNs>((l) => l.navigation.userManagement))} />
              </StyledListItem>
            )}

            {isUserSuperAdmin && hasSkllsAccess && (
              <StyledListItem
                component={Link as ElementType}
                to={AppRoutes.CompaniesManagement}
                selected={!!companyManagementRouteMatch}
              >
                <StyledListItemIcon>
                  <BusinessIcon />
                </StyledListItemIcon>
                <DrawerListItemText primary={translate(nameof.full<I18nCommonNs>((l) => l.navigation.companyManagement))} />
              </StyledListItem>
            )}

            {isUserAnyAdmin && hasSkllsAccess && (
              <StyledListItem
                component={Link as ElementType}
                to={AppRoutes.GroupManagement}
                selected={!!groupManagementRouteMatch}
              >
                <StyledListItemIcon>
                  <Icons.SupervisedUserCircle />
                </StyledListItemIcon>
                <DrawerListItemText primary={translate(nameof.full<I18nCommonNs>((l) => l.navigation.groupManagement))} />
              </StyledListItem>
            )}

            {isUserAnyAdmin && hasSkllsAccess && (
              <StyledListItem
                component={Link as ElementType}
                to={AppRoutes.CourseManagement}
                selected={!!courseManagementRouteMatch}
              >
                <StyledListItemIcon>
                  <Icons.School />
                </StyledListItemIcon>
                <DrawerListItemText primary={translate(nameof.full<I18nCommonNs>((l) => l.navigation.courseManagement))} />
              </StyledListItem>
            )}
            {hasSkllsAccess && (
              <StyledListItem
                component={Link as ElementType}
                to={AppRoutes.MyCourses}
                selected={!!myCoursesRouteMatch}
              >
                <StyledListItemIcon>
                  <Icons.LocalLibrary />
                </StyledListItemIcon>
                <DrawerListItemText primary={translate(nameof.full<I18nCommonNs>((l) => l.navigation.courseStatus))} />
              </StyledListItem>
            )}

            {isUserAnyAdmin && hasSkllsAccess && (
              <StyledListItem
                component={Link as ElementType}
                to={AppRoutes.GlobalAssetsLibrary}
                selected={!!globalAssetsLibraryRouteMatch}
              >
                <StyledListItemIcon>
                  <Icons.LibraryAddCheck />
                </StyledListItemIcon>
                <DrawerListItemText primary={translate(nameof.full<I18nCommonNs>((l) => l.navigation.globalAssetsLibrary))} />
              </StyledListItem>
            )}

            {isUserAnyAdmin && hasSkllsAccess && (
              <StyledListItem
                component={Link as ElementType}
                to={AppRoutes.CallManagement}
                selected={!!callsRouteMatch}
              >
                <StyledListItemIcon>
                  <Icons.Phone />
                </StyledListItemIcon>
                <DrawerListItemText primary={translate(nameof.full<I18nCommonNs>((l) => l.navigation.scheduledCalls))} />
              </StyledListItem>
            )}

            {isUserAnyAdmin && hasExercisesAccess && (
              <StyledListItem
                component={Link as ElementType}
                to={AppRoutes.ExercisesManager}
                selected={!!exercisesRouteMatch}
              >
                <StyledListItemIcon>
                  <Icons.EventNote />
                </StyledListItemIcon>
                <DrawerListItemText primary={translate(nameof.full<I18nCommonNs>((l) => l.navigation.exercises))} />
              </StyledListItem>
            )}

            {(hasInterpreterAccess || ((isUserSuperAdmin || isUserOwner) && hasSkllsAccess)) && (
              <StyledListItem
                component={Link as ElementType}
                to={AppRoutes.Translator}
                selected={!!translatorRouteMatch}
              >
                <StyledListItemIcon>
                  <Icons.Translate />
                </StyledListItemIcon>
                <DrawerListItemText primary={translate(nameof.full<I18nCommonNs>((l) => l.navigation.translator))} />
              </StyledListItem>
            )}

            {isUserAnyAdmin && hasSkllsAccess && (
              <StyledListItem
                component={Link as ElementType}
                to={AppRoutes.EvaluationProtocolManager}
                selected={!!evaluationProtocolsRouteMatch}
              >
                <StyledListItemIcon>
                  <Icons.Assessment />
                </StyledListItemIcon>
                <DrawerListItemText primary={translate(nameof.full<I18nCommonNs>((l) => l.navigation.evaluationProtocols))} />
              </StyledListItem>
            )}

            {isUserAnyAdmin && hasSkllsAccess && (
              <StyledListItem
                component={Link as ElementType}
                to={AppRoutes.PersonasManager}
                selected={!!personasRouteMatch}
              >
                <StyledListItemIcon>
                  <Icons.Person />
                </StyledListItemIcon>
                <DrawerListItemText primary="Personas" />
              </StyledListItem>
            )}

            {hasSkllsAccess && (
              <StyledListItem
                component={Link as ElementType}
                to={AppRoutes.UserGuides}
                selected={!!userGuidesRouteMatch}
              >
                <StyledListItemIcon>
                  <Icons.Help />
                </StyledListItemIcon>
                <DrawerListItemText primary={translate(nameof.full<I18nCommonNs>((l) => l.navigation.userGuides))} />
              </StyledListItem>
            )}

            {isUserSuperAdmin && hasSkllsAccess && (
              <StyledListItem
                component={Link as ElementType}
                to={AppRoutes.RecentJobs}
                selected={!!failedJobsRouteMatch}
              >
                <StyledListItemIcon>
                  <Icons.ErrorOutline />
                </StyledListItemIcon>
                <DrawerListItemText primary={translate(nameof.full<I18nCommonNs>((l) => l.navigation.recentJobs), 'Recent Jobs')} />
              </StyledListItem>
            )}
          
          {isUserSuperAdmin && hasSkllsAccess && <StatisticsSection />}
          </StyledList>

        </Box>

        <Box sx={{ paddingInline: theme.spacing(2) }}>
          <Divider />
          <StyledListItem
            component="button"
            aria-label={translate(nameof.full<I18nCommonNs>((l) => l.signOut))}
            sx={{ marginTop: theme.spacing(2), width: '100%' }}
            onClick={logout}>
            <StyledListItemIcon>
              <Icons.Logout />
            </StyledListItemIcon>
            <DrawerListItemText primary={translate(nameof.full<I18nCommonNs>((l) => l.signOut))} />
          </StyledListItem>
        </Box>
      </StyledDrawer>
    </Fragment>
  );
}
